import React from "react";
import {
  Box,
  Text,
  Heading,
  Flex,
  Grid,
  Table,
  Thead,
  Tr,
  Th,
  Tbody,
  Td,
} from "@chakra-ui/react";
import { ArrowForwardIcon } from "@chakra-ui/icons";
import { BadgeImage } from "../../components/BadgeImage";
import { PracticeIcon } from "../../components/Icons/PracticeIcon";
import { BadgesIcon } from "../../components/Icons/BadgesIcon";
import { useHome } from "./useHome";
import { LevelCard } from "../../components/LevelCard";
import { SectionCard } from "../../components/SectionCard";
import { QSetActions, EmptyCell } from "../../components/QSetActions";

export const HomePage = ({ user, userRole, loadingCallback }) => {
  const {
    myApp,
    qsets,
    currentQSet,
    myBadges,
    levels,
    selectedLevel,
    appid,
    userid,
    isPreviewMode,
    isDirectMode,
    history,
    group,
    qAppLevelStatus,
    isLoading,
    getRadioProps,
    continuePreviousTestOrcreateQset,
    startLearning,
  } = useHome({ user, loadingCallback });
  const appName = sessionStorage.getItem("appName");
  const canPractice = userRole === "Student" || isPreviewMode || isDirectMode;
  const hasLevelProgression =
    Array.isArray(myApp?.levelStatus) && myApp.levelStatus.length > 0;
  const isGenerationLockedForProgression =
    hasLevelProgression && Boolean(qAppLevelStatus[selectedLevel]);
  const selectedLevelIndex = levels.findIndex((level) => level.name === selectedLevel);
  const nextLevel = selectedLevelIndex > -1 ? levels[selectedLevelIndex + 1] : null;
  const questionCount = myApp?.app?.bundleAppProp?.maxNumberOfQs;
  const lockMessage = nextLevel?.criteria
    ? `You have exhausted ${selectedLevel}'s quota for generating new questions. Please answer total ${nextLevel.criteria} questions correctly to unlock next level. You can try again the questions.`
    : "You have exhausted this level's quota for generating new questions.";

  return (
    <Box padding={{ base: "20px 10px", md: "20px 20px" }}>
      {/* <Text>{myApp?.app?.bundleDescription}</Text> */}
      {canPractice && (
        <Box className="practice-hero">
          <Heading as="h1" className="practice-hero__title">
            {appName}
          </Heading>
          <Text className="practice-hero__subtitle">
            Pick a level, then choose how you want to practice.
          </Text>
          {!isLoading && levels.length > 0 && (
            <Flex {...group} className="level-tabs" role="radiogroup" aria-label="Level">
              {React.Children.toArray(
                levels.map((level, index) => {
                  const radio = getRadioProps({
                    value: level.name,
                    isDisabled: hasLevelProgression ? !myApp?.levelStatus[index] : false,
                  });
                  return (
                    <LevelCard {...radio} criteria={level.criteria}>
                      {level.name}
                    </LevelCard>
                  );
                }),
              )}
            </Flex>
          )}
          <Grid className="mode-cards">
            <ModeCard
              icon="✨"
              title="Learn"
              description="One question at a time. See right away if you got it right, and ask Ninja to explain."
              action={currentQSet?.id ? "Continue in Learn mode" : "Start learning"}
              onClick={startLearning}
              isLocked={isGenerationLockedForProgression}
              isPrimary
            />
            <ModeCard
              icon="⏱"
              title="Practice test"
              description={`A timed set of ${questionCount ? `${questionCount} ` : ""}questions. Answers are revealed when you finish.`}
              action={currentQSet?.id ? "Continue previous test" : "Generate question set"}
              onClick={() => continuePreviousTestOrcreateQset()}
              isLocked={isGenerationLockedForProgression}
            />
          </Grid>
          {isGenerationLockedForProgression && (
            <Text className="practice-hero__lock" role="note">
              🔒 {lockMessage}
            </Text>
          )}
        </Box>
      )}
      <Box className="page-sections">
        <SectionCard
          title="Practice Tests"
          subtitle="Your most recent question sets"
          icon={<PracticeIcon />}
          action={{
            label: "View all",
            onClick: () =>
              history.push(`/previoustests?appid=${appid}&userid=${userid}${isPreviewMode ? "&preview_mode=1" : ""}${isDirectMode ? "&direct_mode=1" : ""}`),
          }}
        >
          {qsets?.length > 0 ? (
            <Box className="qset-table-wrap">
              <LastThreeQsets
                canPractice={canPractice}
                qsets={qsets}
                appid={appid}
                userid={userid}
                isPreviewMode={isPreviewMode}
                isDirectMode={isDirectMode}
                history={history}
                practiceQset={(id, status) => {
                  sessionStorage.setItem(
                    "fromPath",
                    `/?appid=${appid}&userid=${userid}${isPreviewMode ? "&preview_mode=1" : ""}${isDirectMode ? "&direct_mode=1" : ""}`
                  )
                  history.push(
                    status === "View Responses"
                      ? `/qreview/?appid=${appid}&userid=${userid}&qsetid=${id}${isPreviewMode ? "&preview_mode=1" : ""}${isDirectMode ? "&direct_mode=1" : ""}`
                      : `/qviewer/?appid=${appid}&userid=${userid}&qsetid=${id}${isPreviewMode ? "&preview_mode=1" : ""}${isDirectMode ? "&direct_mode=1" : ""}`,
                  )
                }}
              />
            </Box>
          ) : (
            <Box className="section-empty">
              <Text className="section-empty__title">No practice tests yet</Text>
              <Text className="section-empty__text">Start learning or generate a question set above, and it will show up here.</Text>
            </Box>
          )}
        </SectionCard>
        <SectionCard
          title="Badges"
          subtitle={
            myBadges?.length
              ? `${myBadges.filter((badge) => !badge.isLocked).length} of ${myBadges.length} unlocked`
              : "Earn badges by answering questions correctly"
          }
          icon={<BadgesIcon />}
        >
          <Box className="badge-grid">
            {React.Children.toArray(
              myBadges?.map((badge) => (
                <Box className={`badge-tile${badge.isLocked ? " badge-tile--locked" : ""}`}>
                  <Box className="badge-tile__image">
                    <BadgeImage
                      src={badge?.badgeImage}
                      alt={badge.badgeName}
                      isLocked={badge.isLocked}
                    />
                  </Box>
                  <Text className="badge-tile__name">{badge.badgeName}</Text>
                  {badge.isLocked ? (
                    <Text className="badge-tile__hint">
                      Answer {badge.criteria} questions correctly to unlock
                    </Text>
                  ) : (
                    <Text className="badge-tile__earned">🎉 Unlocked</Text>
                  )}
                </Box>
              )),
            )}
          </Box>
        </SectionCard>
      </Box>
    </Box>
  );
};

// One way to practice (Learn / Practice test), shown as a large clickable card.
const ModeCard = ({ icon, title, description, action, onClick, isLocked, isPrimary }) => (
  <Box
    as="button"
    type="button"
    className={`mode-card${isPrimary ? " mode-card--primary" : ""}`}
    onClick={isLocked ? undefined : onClick}
    disabled={isLocked}
    aria-disabled={isLocked}
  >
    <Box as="span" className="mode-card__icon" aria-hidden="true">{icon}</Box>
    <Box as="span" className="mode-card__title">{title}</Box>
    <Box as="span" className="mode-card__text">{description}</Box>
    <Box as="span" className="mode-card__action">
      {action} <ArrowForwardIcon />
    </Box>
  </Box>
);

const LastThreeQsets = ({ qsets, practiceQset, canPractice, appid, userid, isPreviewMode, isDirectMode, history }) => {
  return (
    <Table className="qset-table">
      <Thead>
        <Tr>
          <Th>Name</Th>
          <Th>Date Of Test</Th>
          <Th>Score</Th>
          <Th>Total Time Taken</Th>
          <Th>Avg Time Per Question</Th>
          {canPractice && <Th><Box as="span" className="sr-only">Actions</Box></Th>}
        </Tr>
      </Thead>
      <Tbody>
        {React.Children.toArray(
          qsets.map((qset) => (
            <Tr>
              <Td className="qset-table__name">{qset.name}</Td>
              <Td>{qset.submittedAt || <EmptyCell />}</Td>
              <Td>
                {qset.score
                  ? <span className="qset-score">{qset.score}</span>
                  : <EmptyCell />}
              </Td>
              <Td>{qset.totalTimeTaken || <EmptyCell />}</Td>
              <Td>{qset.avgTimeTaken || <EmptyCell />}</Td>
              {canPractice && (
                <Td>
                  <QSetActions
                    status={qset.status}
                    onOpen={() => practiceQset(qset.id, qset.status)}
                    onTryAgain={() => history.push(`/qviewer/?appid=${appid}&userid=${userid}&qsetid=${qset.id}${isPreviewMode ? "&preview_mode=1" : ""}${isDirectMode ? "&direct_mode=1" : ""}`)}
                  />
                </Td>
              )}
            </Tr>
          )),
        )}
      </Tbody>
    </Table>
  );
};
